import { ApolloError, useQuery } from "@apollo/client";
import { useEffect, useMemo, useState } from "react";
import {
  ComplianceProgramState,
  GetComplianceProgramLoadPreviewQuery,
  GetComplianceProgramLoadPreviewQueryVariables,
} from "types/graphql-schema";

import getComplianceProgramLoadPreviewQuery from "@apps/programs/graphql/get-compliance-program-load-preview-query";
import useDebounce from "@components/use-debounce/use-debounce";

import { convertProgramFormToMutationVariables } from "./form-data";
import { LoadPreview } from "./form-tabs/readiness-figures";
import { ProgramForm } from "./types";
import { missingSelectionField } from "./use-program-form-timeline";

export type ProgramFormLoadPreview = {
  /** The answer, or undefined while there has never been one for these values. */
  preview: LoadPreview | undefined;
  loading: boolean;
  error: ApolloError | undefined;
  /** What the backend refused to answer, in its own words, or null. */
  refusal: string | null;
  /** True while the rule names nobody yet, so nothing is asked. */
  waitingForASelection: boolean;
};

/**
 * How much work the review on this form asks of people, and who alone can
 * change it, recomputed as the admin edits.
 *
 * Read by the Readiness figures and by the "Who can step in" note under the
 * Summary. Both ask with the same variables at the same moment, so Apollo sends
 * one request and hands both the same answer.
 *
 * Nothing here saves. The query takes the form's current values as arguments and
 * hands back an answer; the review is written only when the admin saves the form.
 */
const useProgramFormLoadPreview = (
  proposedComplianceProgram: ProgramForm,
): ProgramFormLoadPreview => {
  // A running review froze its roster and locked its templates, so it is counted
  // as the Timeline panel plans it: from the saved roster and the saved templates.
  const isRunning =
    proposedComplianceProgram.id !== undefined &&
    proposedComplianceProgram.state !== ComplianceProgramState.Draft;

  // The same variables as the participants preview, so both ask about the same
  // unsaved form.
  const variables =
    useMemo<GetComplianceProgramLoadPreviewQueryVariables>(() => {
      const saved = convertProgramFormToMutationVariables(
        proposedComplianceProgram,
        !isRunning,
      );
      return {
        organizationId: proposedComplianceProgram.organizationId,
        complianceProgramId: proposedComplianceProgram.id,
        steps: saved.steps,
        assessmentTemplates: saved.assessmentTemplates,
        calibration: saved.calibration,
        ongoing: saved.ongoing,
        startDateOffset: saved.startDateOffset,
        appliesTo: saved.appliesTo,
        appliesToTeams: saved.appliesToTeams,
        appliesToManagers: saved.appliesToManagers,
        appliesToUsers: saved.appliesToUsers,
        excludedUsers: saved.excludedUsers,
        excludedHireDateAfter: saved.excludedHireDateAfter,
        removeExcludedHireDateAfter: saved.removeExcludedHireDateAfter,
        savedRoster: isRunning,
      };
    }, [proposedComplianceProgram, isRunning]);

  // Debounced on the values rather than on the object, because every keystroke
  // anywhere in the form hands this hook a new object with the same values.
  const liveKey = JSON.stringify(variables);
  const debouncedKey = useDebounce(liveKey, 500);
  const debouncedVariables = useMemo(
    () =>
      JSON.parse(debouncedKey) as GetComplianceProgramLoadPreviewQueryVariables,
    [debouncedKey],
  );

  // A rule that names nobody yet has nothing to resolve, so it is answered here
  // instead of by the backend. A running review never waits: it counts its
  // frozen roster, not the rule.
  const missingField =
    missingSelectionField[proposedComplianceProgram.appliesTo];
  const waitingForASelection = Boolean(
    !isRunning &&
      missingField &&
      (proposedComplianceProgram[missingField] as unknown[]).length === 0,
  );

  const { data, loading, error } = useQuery<
    GetComplianceProgramLoadPreviewQuery,
    GetComplianceProgramLoadPreviewQueryVariables
  >(getComplianceProgramLoadPreviewQuery, {
    fetchPolicy: "cache-and-network",
    variables: debouncedVariables,
    skip: waitingForASelection,
  });

  // Apollo drops the data it holds the moment the variables change, so the last
  // answer is kept here: it is what stays on screen while the next one is being
  // fetched, instead of the figures blinking out on every edit.
  const [remembered, setRemembered] = useState<LoadPreview | undefined>(
    undefined,
  );
  const fromServer = data?.complianceProgramLoadPreview;
  useEffect(() => {
    if (fromServer) {
      setRemembered(fromServer);
    }
  }, [fromServer]);
  // The backend's own words when it has any: a rule reaching outside the admin's
  // tree is refused with a sentence saying what to change.
  const refusal = error?.graphQLErrors?.[0]?.message ?? null;
  // A refusal is about the selection on screen, so the figures for an earlier
  // selection must not stay under it.
  const preview = fromServer ?? (refusal ? undefined : remembered);

  return { preview, loading, error, refusal, waitingForASelection };
};

export default useProgramFormLoadPreview;
